import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useChurchData } from "@/hooks/use-church-data";
import { fetchPrayerRequestsByChurchCode } from "@/services/admin/prayerRequestService";
import { fetchCongregationUsers } from "@/services/admin/congregationUserService";
import { fetchBraceletActivityData, fetchBraceletCodes } from "@/services/admin/braceletService";
import { getTotalScansForChurch } from "@/services/admin/scanService";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";

export function useCongregationData() {
  const { toast } = useToast();
  const session = useSession();
  const [loading, setLoading] = useState(true);
  const [showBraceletCodes, setShowBraceletCodes] = useState(false);
  const [braceletCodes, setBraceletCodes] = useState<string[]>([]);
  const [prayerRequests, setPrayerRequests] = useState<any[]>([]);
  const [congregationUsers, setCongregationUsers] = useState<any[]>([]);
  const [loadingPrayers, setLoadingPrayers] = useState(false);
  const [braceletActivity, setBraceletActivity] = useState<any[]>([]);
  const { congregationName, churchCode, logo } = useChurchData();
  const [congregationStats, setCongregationStats] = useState({
    userCount: 0,
    braceletScans: 0,
    journalEntries: 0
  });

  const fetchCongregationData = async () => {
    if (!churchCode) return;
    
    setLoading(true);
    try {
      console.log("Fetching congregation data for church code:", churchCode);
      
      // Fetch congregation users
      const users = await fetchCongregationUsers(churchCode);
      setCongregationUsers(users);
      
      // Fetch prayer requests
      await fetchPrayerRequests();
      
      // Fetch bracelet activity and update it immediately
      await updateBraceletActivity();
      
      // Fetch bracelet codes
      const codes = await fetchBraceletCodes(churchCode);
      setBraceletCodes(codes);
      
      // Update congregation stats (userCount is already set in updateBraceletActivity)
      setCongregationStats(prevStats => ({
        ...prevStats,
        userCount: users.length
      }));
      
    } catch (error) {
      console.error("Error fetching congregation data:", error);
      toast({
        title: "Error",
        description: "Failed to load congregation data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPrayerRequests = async () => {
    setLoadingPrayers(true);
    try {
      const requests = await fetchPrayerRequestsByChurchCode(churchCode || '');
      setPrayerRequests(requests);
    } catch (error) {
      console.error("Error fetching prayer requests:", error);
    } finally {
      setLoadingPrayers(false);
    }
  };

  const updateBraceletActivity = async () => {
    try {
      // Fetch bracelet activity
      const activity = await fetchBraceletActivityData(churchCode || '');
      setBraceletActivity(activity);
      
      // Get total scans count for this church
      const totalScans = await getTotalScansForChurch(churchCode || '');
      console.log("Total bracelet scans for church:", totalScans);
      
      // Update congregation stats
      setCongregationStats(prevStats => ({
        ...prevStats,
        braceletScans: totalScans,
        journalEntries: activity.reduce((total, bracelet) => total + bracelet.journal_count, 0)
      }));
    } catch (error) {
      console.error("Error updating bracelet activity:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (churchCode) {
      fetchCongregationData();
      
      // We don't need to call enable_realtime from the client since we've already
      // set it up in the database migration
    }
  }, [churchCode]);

  // Set up real-time subscriptions for bracelet scans
  useEffect(() => {
    if (!churchCode) return;

    console.log(`Setting up realtime subscription for bracelet scans with church_code=${churchCode}`);
    
    const channel = supabase.channel('bracelet-scans-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bracelet_scans',
          filter: `church_code=eq.${churchCode}`,
        },
        async (payload) => {
          console.log("Bracelet scan detected in realtime:", payload);
          // Update bracelet activity and stats
          await updateBraceletActivity();
          
          // Show toast notification for new scans
          if (payload.eventType === 'INSERT') {
            toast({
              title: "New Bracelet Scan",
              description: `Bracelet ${payload.new.bracelet_id} was just scanned`,
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    return () => {
      console.log('Removing bracelet scans channel');
      supabase.removeChannel(channel);
    };
  }, [churchCode, toast]);

  return {
    loading,
    showBraceletCodes,
    setShowBraceletCodes,
    braceletCodes,
    prayerRequests,
    congregationUsers,
    loadingPrayers,
    braceletActivity,
    congregationName,
    churchCode,
    logo,
    congregationStats,
    fetchCongregationData,
    updateBraceletActivity,
    viewBraceletCodes: () => setShowBraceletCodes(true)
  };
}
